/**
 * Schémas Zod — Lookup / dedup company & project (D36, Chunk 9.G phase 1).
 *
 * Posture : exact-match normalisé READ-ONLY / SUGGESTION-ONLY. Aucun side-effect,
 * aucun auto-link, aucun merge. Cf. `docs/design/lookup-and-dedup.md`.
 *
 * Phase 1 — décisions figées :
 *   - Normalisation côté TS uniquement (pas de `unaccent` SQL — décision
 *     DL-01 reportée). On génère des variantes (forme normalisée +
 *     éventuellement variantes sans suffixe corporatif) à matcher via `IN`.
 *   - Pas de pg_trgm, pas de fuzzy, pas de scoring multi-canal pondéré.
 *   - `confidence` figé en enum `high|medium|low` (DL-06).
 *   - Soft-deleted exclus en dur (DL-03 phase 1).
 *
 * Phase 2 (9.G.1) ajoutera : trigram, scoring pondéré, `unaccent`, etc.
 */

import { z } from "zod";

// =============================================================================
// Constantes — exposées pour l'API et MCP
// =============================================================================

/**
 * Suffixes corporatifs FR/EN courants strippés lors de la normalisation
 * `legal_name`. Liste figée — modifier ici met à jour API + MCP simultanément.
 *
 * Pour l'extension SQL de cette liste (function `app.normalize_legal_name`),
 * voir 9.G.1 + migration ad hoc. Phase 1 : normalisation TS-only.
 */
export const COMPANY_LEGAL_NAME_SUFFIXES = [
  "inc",
  "ltd",
  "ltée",
  "ltee",
  "corp",
  "corporation",
  "cie",
  "co",
  "enr",
  "srl",
  "sarl",
  "sa",
  "sas",
  "sasu",
  "llc",
  "plc",
] as const;

/**
 * Toutes les `match_reason` retournées par le lookup phase 1.
 * Si tu en ajoutes une, ajoute aussi le code qui la produit côté API.
 */
export const MATCH_REASONS = [
  // Companies
  "neq_exact",
  "business_number_exact",
  "legal_name_postal_exact",
  "phone_exact",
  "email_exact",
  "legal_name_exact_no_postal",
  // Projects
  "reference_code_exact",
  "company_effect_date_exact",
  "company_title_match",
  // Contacts (Chunk 9.J.1 — D36 lookup contacts read-only)
  "contact_email_exact",
  "contact_phone_exact",
  "contact_full_name_exact",
  "contact_full_name_company_exact",
  // Fuzzy trigram (Chunk 9.J.2.B — pg_trgm runtime).
  // Émis quand le canal trigram retourne un row avec
  // `similarity(col, q) >= 0.4` (PO-Q1) ; score plafonné à 0.7 (PO-Q2)
  // pour ne JAMAIS atteindre `high`. Un row peut accumuler exact + trgm.
  "legal_name_trgm",
  "title_trgm",
  "contact_full_name_trgm",
] as const;
export type MatchReason = (typeof MATCH_REASONS)[number];

export const ConfidenceSchema = z.enum(["high", "medium", "low"]);
export type Confidence = z.infer<typeof ConfidenceSchema>;

// =============================================================================
// Helpers de normalisation — pure functions, testables sans DB
// =============================================================================

/**
 * Normalise un `legal_name` company pour comparaison exacte.
 *
 * Étapes :
 *   1. NFKC
 *   2. lowercase
 *   3. strip accents (NFD + retrait `\p{M}`)
 *   4. strip ponctuation `.,;:'"\-_/\\&`
 *   5. collapse whitespace
 *   6. trim
 *
 * Ne strippe PAS les suffixes corporatifs — c'est `stripLegalNameSuffix` qui le
 * fait, séparément, pour qu'on puisse comparer "avec" et "sans" suffixe.
 */
export function normalizeLegalName(input: string): string {
  return input
    .normalize("NFKC")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/[.,;:'"\-_/\\&]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Retourne la forme sans suffixe corporatif (si présent) du `legal_name` déjà
 * normalisé via `normalizeLegalName`. Si aucun suffixe matche, retourne
 * l'entrée inchangée.
 *
 * Convention : on strippe UNIQUEMENT le dernier mot s'il fait partie de
 * `COMPANY_LEGAL_NAME_SUFFIXES`. On ne strippe pas plusieurs suffixes en
 * cascade (rare en pratique, ambigu).
 */
export function stripLegalNameSuffix(normalized: string): string {
  const tokens = normalized.split(" ");
  if (tokens.length < 2) return normalized;
  const last = tokens[tokens.length - 1];
  if (last && (COMPANY_LEGAL_NAME_SUFFIXES as readonly string[]).includes(last)) {
    return tokens.slice(0, -1).join(" ");
  }
  return normalized;
}

/**
 * Génère les variantes pertinentes pour matcher un `legal_name` :
 *   - normalisé brut
 *   - normalisé sans suffixe (si différent)
 *
 * Utilisé pour construire un `WHERE legal_name_normalized IN (...)` côté API.
 * Doublons supprimés.
 */
export function legalNameVariants(input: string): string[] {
  const norm = normalizeLegalName(input);
  if (!norm) return [];
  const stripped = stripLegalNameSuffix(norm);
  return stripped === norm ? [norm] : [norm, stripped];
}

/** NEQ / business_number → digits-only. Vide si la chaîne ne contient aucun chiffre. */
export function normalizeBusinessNumber(input: string): string {
  return input.replace(/\D/g, "");
}

/** Email → trim + lowercase. Pas de regex de validation ici (Zod le fait en amont). */
export function normalizeEmail(input: string): string {
  return input.trim().toLowerCase();
}

/** Phone → digits-only. Compatible international (+1, etc.). */
export function normalizePhone(input: string): string {
  return input.replace(/\D/g, "");
}

/**
 * Code postal canadien — uppercase + retrait des espaces.
 * Format attendu : `H2X 1Y4` ou `H2X1Y4` → `H2X1Y4`.
 */
export function normalizePostalCa(input: string): string {
  return input.toUpperCase().replace(/\s+/g, "");
}

/**
 * Full name — Chunk 9.J.1 (D36 lookup contacts).
 *
 * Réutilise `normalizeLegalName` (NFKC + lowercase + accents + ponctuation +
 * collapse whitespace + trim). C'est volontaire : on veut le même socle que
 * pour le legal_name companies (parité de comportement, simple à raisonner).
 *
 * NE strippe PAS de suffixe — il n'y a pas de notion de suffixe corporatif
 * sur un contact humain. La fonction est exposée séparément pour pouvoir
 * évoluer indépendamment plus tard (gestion accents tirets composés, etc.).
 */
export function normalizeFullName(input: string): string {
  return normalizeLegalName(input);
}

// =============================================================================
// Query schemas — endpoints API
// =============================================================================

/**
 * Base schema SANS le refine "≥1 filtre" — destiné à zValidator côté API
 * (Chunk 9.K.2, parité 9.K.1 lookup-contacts).
 *
 * Pourquoi splitter : le refine top-level produit un `ZodError` que
 * zValidator/errorHandler global convertit en `validation_error` plat sans
 * porter le code spécifique `LOOKUP_COMPANIES_FILTER_REQUIRED` ni le payload
 * bilingue ADR 0027 P-3. On passe donc le base à zValidator (filtres
 * optionnels) et le handler applique la règle "≥1 filtre" via
 * `buildBilingualError`.
 *
 * `LookupCompanyQuerySchema` (avec refine) reste exporté pour typage strict.
 */
export const LookupCompanyQueryBaseSchema = z.object({
  q: z
    .string()
    .min(2)
    .max(200)
    .optional()
    .describe("Terme libre — assimilé à legal_name pour exact-match phase 1"),
  legal_name: z.string().min(2).max(200).optional(),
  postal_code: z.string().min(3).max(10).optional(),
  neq: z.string().min(3).max(50).optional().describe("NEQ ou business_number"),
  phone: z.string().min(7).max(30).optional(),
  email: z.string().email().max(254).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(10),
});

/**
 * Filtres acceptés sur GET /v1/lookup/companies.
 * Au moins UN filtre obligatoire (refine) pour éviter qu'un caller dump la
 * table via top-N.
 */
export const LookupCompanyQuerySchema = LookupCompanyQueryBaseSchema.refine(
  (v) => Boolean(v.q || v.legal_name || v.postal_code || v.neq || v.phone || v.email),
  {
    // 9.K.2 — refine technique EN (P-2 ADR 0027). Le wording user-facing
    // FR/EN vit dans le registre (ERROR_CODES.LOOKUP_COMPANIES_FILTER_REQUIRED) ;
    // ce message-ci sert seulement de fallback de dev pour Zod.
    message: "At least one filter required: q, legal_name, postal_code, neq, phone, or email.",
  },
);
export type LookupCompanyQuery = z.infer<typeof LookupCompanyQuerySchema>;

/**
 * Base schema SANS le refine "≥1 filtre" — destiné à zValidator côté API
 * (Chunk 9.K.2, parité 9.K.1 lookup-contacts).
 *
 * Cf. doctrine `LookupCompanyQueryBaseSchema` ci-dessus.
 */
export const LookupProjectQueryBaseSchema = z.object({
  reference_code: z
    .string()
    .regex(/^PRJ-\d{4}-\d{6}$/, "Format attendu : PRJ-YYYY-NNNNNN")
    .optional(),
  q: z.string().min(2).max(200).optional().describe("Terme libre — assimilé à title"),
  company_id: z.string().uuid().optional(),
  /**
   * Date d'effet du projet à comparer (canal company+effect_date).
   * Phase 1 : on compare égalité stricte sur `effect_date`.
   */
  effect_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Format attendu : YYYY-MM-DD")
    .optional(),
  limit: z.coerce.number().int().min(1).max(50).default(10),
});

/**
 * Filtres acceptés sur GET /v1/lookup/projects.
 * Au moins UN filtre obligatoire.
 *
 * Court-circuit : si `reference_code` fourni, on retourne un seul candidat
 * (le projet matchant exactement) et on ignore les autres filtres.
 */
export const LookupProjectQuerySchema = LookupProjectQueryBaseSchema.refine(
  (v) => Boolean(v.reference_code || v.q || v.company_id || v.effect_date),
  {
    // 9.K.2 — refine technique EN (P-2 ADR 0027). Wording user-facing dans
    // ERROR_CODES.LOOKUP_PROJECTS_FILTER_REQUIRED.
    message: "At least one filter required: reference_code, q, company_id, or effect_date.",
  },
);
export type LookupProjectQuery = z.infer<typeof LookupProjectQuerySchema>;

/**
 * Filtres acceptés sur GET /v1/lookup/contacts (Chunk 9.J.1).
 *
 * Au moins UN filtre obligatoire (refine). Décisions PO 9.J.1 :
 *  - PO-Q1 : `q` libre normalisé, recherche `full_name` exact normalisé.
 *  - PO-Q2 : `phone` digits-only strict (parité 9.G).
 *  - PO-Q3 : `email` / `phone` peuvent être utilisés sans `company_id` —
 *    isolation tenant suffit (RLS).
 *  - PO-Q4 : `q` seul accepté → score `low` + warning meta côté handler.
 */
/**
 * Base schema SANS le refine "≥1 filtre" — destiné à zValidator côté API.
 *
 * Pourquoi splitter (parité `DocumentLinkListQuerySchema` 9.F.1) :
 *   - Le refine top-level produit un `ZodError` que zValidator intercepte
 *     en `{success:false, error:{issues:[...]}}` (format Zod plat) ou que
 *     l'`errorHandler` global convertit en `{code:"validation_error",...}`
 *     plat. Aucun des deux ne porte le code spécifique
 *     `LOOKUP_CONTACTS_FILTER_REQUIRED` ni le payload bilingue P-3.
 *   - 9.K.1 doit ratifier ADR 0027 sur le cas d'erreur principal de 9.J.1.
 *     On passe donc à zValidator ce schema base (filtres optionnels), et
 *     le handler applique la règle "≥1 filtre" via `buildBilingualError`.
 *
 * `LookupContactQuerySchema` (avec refine) reste exporté ci-dessous pour
 * tout consommateur qui veut valider strictement (tests, MCP tool).
 */
export const LookupContactQueryBaseSchema = z.object({
  q: z
    .string()
    .min(2)
    .max(200)
    .optional()
    .describe("Terme libre — assimilé à full_name pour exact-match phase 1"),
  full_name: z.string().min(2).max(200).optional(),
  email: z.string().email().max(254).optional(),
  phone: z.string().min(7).max(30).optional(),
  company_id: z
    .string()
    .uuid()
    .optional()
    .describe("Restreint la recherche aux contacts d'une compagnie donnée"),
  limit: z.coerce.number().int().min(1).max(50).default(10),
});

export const LookupContactQuerySchema = LookupContactQueryBaseSchema.refine(
  (v) => Boolean(v.q || v.full_name || v.email || v.phone || v.company_id),
  {
    // 9.K.1 — refine technique EN (P-2 ADR 0027). Le wording user-facing
    // FR/EN vit dans le registre (ERROR_CODES.LOOKUP_CONTACTS_FILTER_REQUIRED) ;
    // ce message-ci sert seulement de fallback de dev pour Zod.
    message: "At least one filter required: q, full_name, email, phone, or company_id.",
  },
);
export type LookupContactQuery = z.infer<typeof LookupContactQuerySchema>;

// =============================================================================
// Response schemas — exposés pour typage API + tests
// =============================================================================

/**
 * Snapshot léger d'une entité — ce qu'on retourne au caller comme `preview`.
 * Pas la row complète : juste de quoi décider quoi faire.
 *
 * Pour company : id + legal_name + display_name + business_number + address_postal.
 * Pour project : id + reference_code + title + company_id + effect_date.
 */
export const CompanyPreviewSchema = z.object({
  id: z.string().uuid(),
  legal_name: z.string(),
  display_name: z.string().nullable(),
  business_number: z.string().nullable(),
  address_postal: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
});
export type CompanyPreview = z.infer<typeof CompanyPreviewSchema>;

export const ProjectPreviewSchema = z.object({
  id: z.string().uuid(),
  reference_code: z.string(),
  title: z.string(),
  company_id: z.string().uuid(),
  effect_date: z.string().nullable(),
  status: z.string(),
});
export type ProjectPreview = z.infer<typeof ProjectPreviewSchema>;

/**
 * Snapshot léger d'un contact (Chunk 9.J.1). Aligné sur `app.contacts` :
 *   id, full_name, title, email, phone, company_id, is_primary.
 *
 * `notes` n'est PAS exposé (pas pertinent pour un suggestion-only, et peut
 * contenir du contexte sensible).
 */
export const ContactPreviewSchema = z.object({
  id: z.string().uuid(),
  full_name: z.string(),
  title: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  company_id: z.string().uuid(),
  is_primary: z.boolean(),
});
export type ContactPreview = z.infer<typeof ContactPreviewSchema>;

export const LookupCandidateSchema = z.object({
  entity_id: z.string().uuid(),
  score: z.number().min(0).max(1),
  confidence: ConfidenceSchema,
  match_reasons: z.array(z.enum(MATCH_REASONS)).min(1),
  preview: z.union([CompanyPreviewSchema, ProjectPreviewSchema, ContactPreviewSchema]),
});
export type LookupCandidate = z.infer<typeof LookupCandidateSchema>;

/**
 * Meta optionnelle : utilisée par /v1/lookup/contacts quand `q` est seul
 * (PO-Q4 9.J.1) pour signaler la baisse de confiance — score `low`.
 * Reste compatible avec /v1/lookup/companies & /v1/lookup/projects (omise).
 *
 * 9.K.1 — Format warnings migré de `string[]` (FR plat) vers
 * `[{ code, messages: { en, fr }, details? }]` structuré bilingue (P-4
 * ADR 0027). Construits côté handler via `buildBilingualWarning(code)`.
 */
export const LookupWarningSchema = z.object({
  code: z.string(),
  messages: z.object({
    en: z.string(),
    fr: z.string(),
  }),
  details: z.unknown().optional(),
});
export type LookupWarning = z.infer<typeof LookupWarningSchema>;

export const LookupResponseMetaSchema = z.object({
  warnings: z.array(LookupWarningSchema).optional(),
});
export type LookupResponseMeta = z.infer<typeof LookupResponseMetaSchema>;

export const LookupResponseSchema = z.object({
  candidates: z.array(LookupCandidateSchema),
  limit: z.number().int().min(1).max(50),
  truncated: z.boolean(),
  meta: LookupResponseMetaSchema.optional(),
});
export type LookupResponse = z.infer<typeof LookupResponseSchema>;

// =============================================================================
// Helper — confidence depuis score
// =============================================================================

/**
 * Bucket figé phase 1 (DL-06) :
 *   score ≥ 0.9 → high
 *   score ≥ 0.7 → medium
 *   sinon       → low
 */
export function scoreToConfidence(score: number): Confidence {
  if (score >= 0.9) return "high";
  if (score >= 0.7) return "medium";
  return "low";
}
